'use client';

import { useEffect, useState } from 'react';
import { WipTag } from '@/components/sections/kit';
import { SHEET, SheetHeader } from './Sheet';
import { useFormGuard } from '@/components/forms/useFormGuard';
import { HUMAN_CHECK_FAILED, useHumanCheck } from '@/components/forms/useHumanCheck';
import { roadmapItem } from '@/lib/roadmap';
import { DEVELOPERS_WEBHOOKS_HREF } from './data';

const webhooks = roadmapItem('webhooks');

/** Three channels as equals. Webhook holds its column with the real Q1 2027
 *  sash — no fake input and no promised mechanics (signing, retries): the
 *  roadmap entry only promises outbound webhooks. */
export default function Subscribe() {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();
  const widgetShown = human.widget !== null;

  /*
   * The success message and the Turnstile widget both change this block's
   * height. GridClearing measures blocks with getBoundingClientRect, so
   * without a nudge the clearing stays sized to the form and grid lines cut
   * through whatever grew past it.
   */
  useEffect(() => {
    window.dispatchEvent(new Event('grid-relayout'));
  }, [state, widgetShown]);

  const send = async (payload: Record<string, unknown>) => {
    setState('sending');
    setErrorMessage(null);
    try {
      // Every submission needs a fresh Turnstile token (website#302).
      const turnstileToken = await human.getToken();
      if (!turnstileToken) {
        setErrorMessage(HUMAN_CHECK_FAILED);
        setState('error');
        return;
      }
      const res = await fetch('/api/status-subscribe', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, turnstileToken }) });
      const body = await res.json().catch(() => ({}));
      if (res.ok) setState('done');
      else {
        setErrorMessage(typeof body.error === 'string' ? body.error : null);
        setState('error');
      }
    } catch {
      setState('error');
    } finally {
      human.reset();
    }
  };

  const col = 'flex flex-col gap-3 px-5 py-5 md:px-6';
  const head = 'font-jetbrains text-[10.5px] tracking-[0.14em] text-dim';
  const input = 'w-full rounded-[9px] border border-line-2 bg-surface px-3.5 py-2 text-[13.5px] text-ink outline-brand-600 placeholder:text-dim';

  return (
    <section aria-labelledby="subscribe-h" data-grid-block data-grid-fill="#ffffff" className={SHEET}>
      <SheetHeader id="subscribe-h" title="SUBSCRIBE" note="EMAIL · RSS · WEBHOOK" />
      <div className="grid divide-y divide-line md:grid-cols-3 md:divide-x md:divide-y-0">
      <div className={col}>
        <span className={head}>EMAIL</span>
        {state === 'done' ? (
          <p className="text-[13.5px] text-muted" role="status">Almost there — check your inbox and confirm.</p>
        ) : (
          <form onFocus={human.arm} onSubmit={(e) => { e.preventDefault(); if (state !== 'sending') void send({ email, ...guardFields() }); }} className="flex flex-col gap-2.5">
            <HoneypotField />
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" aria-label="Email address" className={input} />
            <button type="submit" disabled={state === 'sending'} className="rounded-[9px] bg-ink px-4 py-2 text-[13.5px] font-semibold text-white disabled:opacity-60">
              {state === 'sending' ? 'Sending…' : 'Email me status updates'}
            </button>
            <span className="text-[12px] text-dim" aria-live="polite">
              {state === 'error' ? errorMessage || 'That didn’t send — try again?' : 'Double opt-in. Incidents and resolutions only, nothing else.'}
            </span>
            {human.widget}
          </form>
        )}
      </div>
      <div className={col}>
        <span className={head}>RSS</span>
        <p className="text-[13.5px] text-muted">No signup. Point any reader at the feed.</p>
        <code className="select-all rounded-[9px] border border-line-2 bg-surface-2 px-3 py-2 font-jetbrains text-[12px] text-ink">https://rii.link/status/feed.xml</code>
      </div>
      <div className={col}>
        <div className="flex items-center gap-2">
          <span className={head}>WEBHOOK</span>
          <WipTag label={webhooks.promised} />
        </div>
        <p className="text-[13.5px] text-muted">
          Status events will come as webhooks once the product&rsquo;s own outbound webhooks ship. Until then, email or RSS reach you first.{' '}
          <a href={DEVELOPERS_WEBHOOKS_HREF} className="font-semibold text-ink underline decoration-line-3 underline-offset-[3px] hover:text-brand-600">Webhooks on the roadmap</a>
        </p>
      </div>
      </div>
    </section>
  );
}
